import type { LucideIcon } from "lucide-react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

export interface StatCardProps {
  label: string;
  value: string | number;
  icon: LucideIcon;
  trend?: string;
  trendUp?: boolean;
  variant?: "primary" | "secondary" | "warning" | "success";
  progress?: number;
  className?: string;
}

const iconBox: Record<NonNullable<StatCardProps["variant"]>, string> = {
  primary: "bg-primary/10 text-primary",
  secondary: "bg-secondary text-secondary-foreground",
  warning: "bg-warning/10 text-warning",
  success: "bg-success/10 text-success",
};

/** StatCard canónico del Design System (F2/F3) — sucesor de ClinicalHeroStatCard/StatCard. */
export function StatCard({
  label,
  value,
  icon: Icon,
  trend,
  trendUp = true,
  variant = "primary",
  progress,
  className,
}: StatCardProps) {
  return (
    <div className={cn("glass-card relative overflow-hidden rounded-xl p-card-padding", className)}>
      <div className="mb-4 flex items-start justify-between">
        <div className={cn("rounded-lg p-3", iconBox[variant])}>
          <Icon className="h-5 w-5" aria-hidden />
        </div>
        {trend ? (
          <span
            className={cn(
              "flex items-center gap-1 text-body-md font-bold",
              trendUp ? "text-primary" : "text-destructive",
            )}
          >
            {trendUp ? (
              <TrendingUp className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <TrendingDown className="h-3.5 w-3.5" aria-hidden />
            )}
            {trend}
          </span>
        ) : null}
      </div>
      <h3 className="text-headline-lg font-bold tabular-nums tracking-tight text-foreground">{value}</h3>
      <p className="mt-1 text-label-md uppercase tracking-wider text-muted-foreground">{label}</p>
      {progress != null ? (
        <div className="absolute bottom-0 left-0 h-1 w-full bg-primary/20">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}
